import React, { createContext, useContext, useState, useCallback } from 'react';
import { getAuthHeaders } from '../lib/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Re-export getAuthHeaders for backward compatibility
export { getAuthHeaders } from '../lib/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [googleNeedsConnect, setGoogleNeedsConnect] = useState(false);

  // Tracks whether the backend appears to be temporarily unreachable.
  // When true, the app shell can render a "Reconnecting to server…" splash
  // instead of treating the user as logged-out (which is what was happening
  // during pod-restart outages — Cloudflare returns 520/502, our auth check
  // got a non-200, and the user was bounced to the login page even though
  // their cookie + MongoDB session were both still valid).
  const [backendUnreachable, setBackendUnreachable] = useState(false);

  const checkAuth = useCallback(async () => {
    // Tries /auth/me with exponential backoff on transient errors (5xx,
    // network failures, Cloudflare 520/502/503/504). Only an explicit 401
    // clears the stored token — anything else is treated as a transient
    // infrastructure blip and we keep the user authenticated optimistically.
    const TRANSIENT_STATUSES = new Set([0, 408, 425, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524]);
    const MAX_ATTEMPTS = 4;
    const BASE_DELAY_MS = 800;

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      let response;
      let networkError = false;
      try {
        response = await fetch(`${API}/auth/me`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
      } catch (err) {
        // Network error — fetch() couldn't reach the server at all.
        networkError = true;
        console.warn(`Auth check network error (attempt ${attempt + 1}/${MAX_ATTEMPTS}):`, err);
      }

      if (!networkError && response.ok) {
        const userData = await response.json();
        setUser(userData);
        setBackendUnreachable(false);
        const needsConnect = userData.google_services?.needs_refresh ||
          (!userData.google_services?.connected && !userData.google_services?.using_shared);
        setGoogleNeedsConnect(!!needsConnect);
        setLoading(false);
        return userData;
      }

      if (!networkError && response.status === 401) {
        // Explicit "you are logged out" from the server. Clear the token.
        localStorage.removeItem('gs_session_token');
        setUser(null);
        setBackendUnreachable(false);
        setLoading(false);
        return null;
      }

      const status = networkError ? 0 : (response?.status ?? 0);
      const transient = TRANSIENT_STATUSES.has(status);
      if (!transient) {
        // Unexpected response shape — fail closed but don't clear token.
        console.warn('Auth check returned unexpected status:', status);
        setUser(null);
        setBackendUnreachable(false);
        setLoading(false);
        return null;
      }

      // Transient infra error → mark backend as unreachable and retry.
      setBackendUnreachable(true);
      if (attempt < MAX_ATTEMPTS - 1) {
        const delay = BASE_DELAY_MS * Math.pow(2, attempt);
        await sleep(delay);
      }
    }

    // Out of retries — leave token in place, leave user state as the last
    // known value. The router can use `backendUnreachable` to show a splash
    // rather than dropping the operator on the login page mid-outage.
    console.warn('Auth check exhausted retries; backend appears unreachable.');
    setLoading(false);
    return null;
  }, []);

  const login = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Unified Google OAuth — single consent grants login + Gmail + Drive + Calendar.
    // Throws on failure so the caller can reset its UI state.
    const res = await fetch(`${API}/auth/google/start`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Sign-in start failed (HTTP ${res.status}). ${txt.slice(0, 200)}`);
    }
    const data = await res.json();
    if (!data.authorization_url) throw new Error('Server did not return a Google authorization URL.');
    window.location.href = data.authorization_url;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear local token
      localStorage.removeItem('gs_session_token');
      setUser(null);
      setGoogleNeedsConnect(false);
    }
  };

  const exchangeSessionId = async (sessionId) => {
    try {
      const response = await fetch(`${API}/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (response.ok) {
        const userData = await response.json();
        // Store session token - always ensure we have one
        const token = userData.session_token || `fallback_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        localStorage.setItem('gs_session_token', token);
        // Ensure the user data has the token for downstream use
        userData.session_token = token;
        setUser(userData);
        
        // Check if Google needs to be connected
        if (userData.google_needs_connect) {
          setGoogleNeedsConnect(true);
        }
        
        return userData;
      } else {
        // Check for unauthorized domain error
        const errorData = await response.json().catch(() => ({}));
        if (errorData.detail === 'UNAUTHORIZED_DOMAIN') {
          throw new Error('UNAUTHORIZED_DOMAIN');
        }
        throw new Error('Session exchange failed');
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const value = {
    user,
    setUser,
    loading,
    setLoading,
    error,
    checkAuth,
    login,
    logout,
    exchangeSessionId,
    isAuthenticated: !!user,
    googleNeedsConnect,
    setGoogleNeedsConnect,
    backendUnreachable
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
